import { reactive, computed, getCurrentInstance } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { listEmployeePayPlans } from "@/api/employeeAPI";
import { listPayRanges } from "@/api/payRangeAPI";
import launchDarklyService from "@/services/launchdarkly";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { getListOfDepartments } from "@/api/departmentAPI";
import { mapPayPlanListRow, hasEmployeeIdentity } from "@components/PayPlansTable/mapPayPlanListRow";
import { toApiDateString } from "@components/PayRangeDetailPage/composables/usePayRangeDetailPageState";
import {
  PAY_PLANS_LIST_SEARCH_PARAM_KEYS,
  chipsFromAppliedPayPlanFilters,
  splitPayPlanFilterChips,
  persistTokensFromChips,
  PAY_PLAN_TOKEN_PREFIX,
  CHANGES_TOKEN_PREFIX,
  DEPT_TOKEN_PREFIX,
} from "@components/PayPlansTable/payPlansTableFilters";

export const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const FILTER_KEY = "payPlansList";
const IN_FORCE_LOOKUP_MAX_PAGES = 25;

/** Stale-response guard: only the most recent fetchRows call may write state. */
let requestSeq = 0;
let activeController = null;

function isCanceledError(err) {
  return (
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    err?.code === "ERR_CANCELED"
  );
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function debounce(fn, delayMs) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delayMs);
  };
}

function searchValuesFromFilters(filters) {
  const next = { employee_name: "", primary_position_name: "" };
  (filters || []).forEach((filter) => {
    if (!PAY_PLANS_LIST_SEARCH_PARAM_KEYS.includes(filter?.fieldKey)) return;
    next[filter.fieldKey] = String(filter.value || "").trim();
  });
  return next;
}

/**
 * Page state for Fair Pay → Pay Plans list (ER-10862).
 */
export function usePayPlansListPageState() {
  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const defaultFilters = getDefaultFiltersForKey(FILTER_KEY) || [];

  const state = reactive({
    rows: [],
    loading: false,
    error: null,
    /** ER-12840 — 403 renders the page-level permission-denied state, not a toast. */
    forbidden: false,
    page: 1,
    pageSize: PAGE_SIZE,
    count: 0,
    currentPayPlanCount: null,
    activePayRangeCount: null,
    /** Pay ranges In Force as of today: null until known (or when the lookup failed). */
    inForcePayRangeCount: null,
    search: {
      employee_name: "",
      primary_position_name: "",
    },
    filters: {
      key: FILTER_KEY,
      chips: [],
      showFilterDialog: false,
      clearSelection: "",
      departmentScope: "subtree",
    },
    departments: [],
    departmentsLoading: false,
    /** "As of" date control (idea-MDP-429). `custom` false means Today, resolved fresh on every fetch. */
    asOf: {
      enabled: false,
      custom: false,
      date: "",
      pickerOpen: false,
    },
  });

  /** Local calendar day — never converted through UTC. */
  function todayLocalIso() {
    return toApiDateString(new Date());
  }

  /** ISO date the list is resolved against (shown in the control, sent as `as_of_date`). */
  const asOfDate = computed(() =>
    state.asOf.custom && state.asOf.date ? state.asOf.date : todayLocalIso()
  );
  const asOfIsToday = computed(() => !state.asOf.custom);

  /** First-run state: the account is known to have no In Force pay range. */
  const noInForcePayRange = computed(() => state.inForcePayRangeCount === 0);

  const pageCount = computed(() =>
    Math.max(1, Math.ceil((state.count || 0) / state.pageSize) || 1)
  );

  const filterModalSelection = computed(() => {
    const split = splitPayPlanFilterChips(state.filters.chips);
    const department = (state.departments || []).find(
      (item) =>
        String(item?.id) === String(split.department) ||
        item?.name === split.department
    );
    return {
      fairPayStatus: split.fairPayStatus,
      payPlan: split.payPlan,
      change: split.changes,
      department: department || null,
      scope: state.filters.departmentScope,
    };
  });

  /** Human-readable message for a failed list fetch (shared by toast + inline error). */
  function resolveErrorMessage(err, fallback = "Failed to load pay plans") {
    return (
      err?.response?.data?.error ||
      err?.response?.data?.detail ||
      err?.message ||
      fallback
    );
  }

  function notifyError(err, fallback = "Failed to load pay plans") {
    const message = resolveErrorMessage(err, fallback);
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error(message);
    }
    return message;
  }

  function applyEnvelopeCounts(data) {
    if (data?.current_pay_plan_count != null) {
      state.currentPayPlanCount = data.current_pay_plan_count;
    }
    if (data?.active_pay_range_count != null) {
      state.activePayRangeCount = data.active_pay_range_count;
    }
  }

  async function fetchRows() {
    const accountId = getAccountId();
    if (!accountId) {
      state.rows = [];
      state.count = 0;
      return;
    }

    const seq = ++requestSeq;
    if (activeController) {
      activeController.abort();
    }
    const controller = new AbortController();
    activeController = controller;

    state.loading = true;
    state.error = null;
    state.forbidden = false;
    try {
      const split = splitPayPlanFilterChips(state.filters.chips);
      const response = await listEmployeePayPlans(accountId, {
        page: state.page,
        pageSize: state.pageSize,
        employee_name: state.search.employee_name || undefined,
        primary_position_name: state.search.primary_position_name || undefined,
        fair_pay_status: split.fairPayStatus || undefined,
        pay_plan: split.payPlan || undefined,
        changes: split.changes || undefined,
        department: split.department || undefined,
        department_scope: split.department
          ? state.filters.departmentScope || "subtree"
          : undefined,
        as_of_date: state.asOf.enabled ? asOfDate.value : undefined,
        signal: controller.signal,
      });

      if (seq !== requestSeq) return;

      const data = response?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      state.rows = results.map(mapPayPlanListRow);
      state.count = data.count ?? results.length;
      applyEnvelopeCounts(data);
    } catch (err) {
      if (isCanceledError(err)) return;
      if (seq !== requestSeq) return;
      state.rows = [];
      state.count = 0;
      if (err?.response?.status === 403) {
        state.forbidden = true;
      } else {
        state.error = notifyError(err);
      }
    } finally {
      if (seq === requestSeq) {
        state.loading = false;
      }
    }
  }

  /**
   * First-run check against the pay ranges list, always one row per request.
   * `as_of` is a version selector, not a row filter: a PUBLISHED range with no
   * version covering today (future-dated or already ended) comes back with a
   * null `compensation_snapshot`, so each row is judged by its snapshot and the
   * next page is read until an In Force one turns up. A failed or unreadable
   * lookup leaves the count unknown so the normal table shows.
   */
  async function loadInForcePayRangeCount() {
    try {
      const asOf = todayLocalIso();
      let total = 1;
      for (let page = 1; page <= Math.min(total, IN_FORCE_LOOKUP_MAX_PAGES); page += 1) {
        // eslint-disable-next-line no-await-in-loop -- each page decides whether to read the next
        const { data } = await listPayRanges({
          page,
          pageSize: 1,
          status: ["PUBLISHED"],
          as_of: asOf,
        });
        if (!Number.isFinite(data?.count)) break;
        if (data.count === 0) {
          state.inForcePayRangeCount = 0;
          return;
        }
        total = data.count;
        const snapshot = data.results?.[0]?.compensation_snapshot;
        if (snapshot?.type === "version") {
          state.inForcePayRangeCount = 1;
          return;
        }
        if (snapshot !== null) break;
        if (page === total) {
          state.inForcePayRangeCount = 0;
          return;
        }
      }
      state.inForcePayRangeCount = null;
    } catch {
      state.inForcePayRangeCount = null;
    }
  }

  async function loadAsOfFlag() {
    try {
      state.asOf.enabled = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.asOf.enabled = false;
    }
  }

  function openAsOfPicker() {
    state.asOf.pickerOpen = true;
  }

  function closeAsOfPicker() {
    state.asOf.pickerOpen = false;
  }

  async function setAsOf(iso, { resetPage }) {
    state.asOf.custom = Boolean(iso) && iso !== todayLocalIso();
    state.asOf.date = state.asOf.custom ? iso : "";
    if (resetPage) state.page = 1;
    await fetchRows();
  }

  async function applyAsOfDate(date) {
    closeAsOfPicker();
    const iso = toApiDateString(date);
    if (!iso) return;
    await setAsOf(iso, { resetPage: true });
  }

  async function resetAsOfToToday() {
    await setAsOf("", { resetPage: true });
  }

  /**
   * A slider ‹ › moved a row to `version`: the version covering today resets As of
   * to Today, any other sets As of to its effective-on date. Stays on the current
   * page. Decided from the version's own ISO dates — `version.state` is resolved
   * against the previous As of date, not today.
   */
  async function applyAsOfFromVersion(version) {
    if (!state.asOf.enabled) return;
    const effectiveOn = toApiDateString(version?.effectiveOn);
    if (!effectiveOn) return;
    const effectiveBy = toApiDateString(version?.effectiveBy);
    const today = todayLocalIso();
    const coversToday = effectiveOn <= today && (!effectiveBy || effectiveBy >= today);
    await setAsOf(coversToday ? "" : effectiveOn, { resetPage: false });
  }

  async function loadDepartments(search = "") {
    const accountId = getAccountId();
    if (!accountId) return;
    state.departmentsLoading = true;
    try {
      const response = await getListOfDepartments(
        accountId,
        search,
        { status: ["active"] },
        { limit: 100, offset: 0 }
      );
      const results = response?.data?.results;
      state.departments = Array.isArray(results) ? results : [];
    } catch {
      state.departments = [];
    } finally {
      state.departmentsLoading = false;
    }
  }

  const searchDepartments = debounce(loadDepartments, SEARCH_DEBOUNCE_MS);

  function persistChips(chips) {
    const next = Array.isArray(chips) ? chips : [];
    state.filters.chips = next;
    filter.setFilters({ key: FILTER_KEY, filters: persistTokensFromChips(next) });
  }

  function loadFiltersOnReload() {
    const saved = filter.getFilters(FILTER_KEY);
    if (saved && saved.length > 0) {
      state.filters.chips = chipsFromAppliedPayPlanFilters(saved);
    } else {
      state.filters.chips = chipsFromAppliedPayPlanFilters(defaultFilters);
    }
  }

  async function performSearch(event) {
    PAY_PLANS_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
      state.search[key] = "";
    });
    const values = searchValuesFromFilters(event);
    state.search.employee_name = values.employee_name;
    state.search.primary_position_name = values.primary_position_name;
    state.page = 1;
    await fetchRows();
  }

  const onSearchFiltersChanged = debounce(performSearch, SEARCH_DEBOUNCE_MS);

  async function onPageChange(page) {
    state.page = page || 1;
    await fetchRows();
  }

  function onOpenMoreFilters() {
    state.filters.showFilterDialog = true;
  }

  function closeFilterModal() {
    state.filters.showFilterDialog = false;
  }

  async function onFiltersApplied(selection = {}) {
    const chips = [];
    const labels = {
      fair: "Fair",
      unfair: "Unfair",
      has_current: "Has Current",
      does_not_have_current: "Does Not Have Current",
      no_pending_changes: "No Pending Changes",
      has_evaluation: "Has Evaluation",
      has_proposal: "Has Proposal",
      has_rejected_proposal: "Has Rejected Proposal",
      has_upcoming: "Has Upcoming",
      not_eligible: "Not Eligible",
      eligible: "Eligible",
    };

    if (selection.fairPayStatus) {
      chips.push({
        key: selection.fairPayStatus,
        label: labels[selection.fairPayStatus],
      });
    }
    if (selection.payPlan) {
      chips.push({
        key: `${PAY_PLAN_TOKEN_PREFIX}${selection.payPlan}`,
        label: labels[selection.payPlan],
      });
    }
    if (selection.change) {
      chips.push({
        key: `${CHANGES_TOKEN_PREFIX}${selection.change}`,
        label: labels[selection.change],
      });
    }
    if (selection.department?.id != null) {
      chips.push({
        key: `${DEPT_TOKEN_PREFIX}${selection.department.id}`,
        label: selection.department.name,
      });
    }

    persistChips(chips);
    state.filters.departmentScope = selection.scope || "subtree";
    state.filters.showFilterDialog = false;
    state.page = 1;
    await fetchRows();
  }

  async function onRemoveFilter(key) {
    const removed = (state.filters.chips || []).find((chip) => chip.key === key);
    persistChips((state.filters.chips || []).filter((chip) => chip.key !== key));
    state.filters.clearSelection = removed?.label || key;
    state.page = 1;
    await fetchRows();
  }

  async function resetFiltersToDefaults() {
    persistChips([]);
    state.filters.clearSelection = "clear-all";
    state.filters.departmentScope = "subtree";
    state.page = 1;
    await fetchRows();
  }

  async function isEmployeePayPlansDrillDownEnabled() {
    try {
      return (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      return false;
    }
  }

  async function onRowClick(row) {
    if (!hasEmployeeIdentity(row) || !proxy?.$router) return;
    const employeeId = row.employee?.id || row.id;
    if (!employeeId) return;
    // Fair Pay MVP (idea-MDP-429): drill into Employee Pay Plans; flag off keeps the legacy card.
    const name = (await isEmployeePayPlansDrillDownEnabled()) ? "EmployeePayPlans" : "FairPayEmployee";
    proxy.$router.push({ name, params: { employeeId: String(employeeId) } }).catch(() => {});
  }

  return {
    state,
    pageCount,
    noInForcePayRange,
    filterModalSelection,
    asOfDate,
    asOfIsToday,
    fetchRows,
    loadInForcePayRangeCount,
    loadAsOfFlag,
    openAsOfPicker,
    closeAsOfPicker,
    applyAsOfDate,
    resetAsOfToToday,
    applyAsOfFromVersion,
    loadDepartments,
    searchDepartments,
    loadFiltersOnReload,
    onSearchFiltersChanged,
    onPageChange,
    onOpenMoreFilters,
    closeFilterModal,
    onFiltersApplied,
    onRemoveFilter,
    resetFiltersToDefaults,
    onRowClick,
  };
}
